import React, { useState, useEffect } from 'react';
import { Sparkles, Book, Scroll, MapPin, Mountain, Eye, Users, Home, Zap } from 'lucide-react';

/**
 * SpiralORG.org Landing Page - Version B: Mystical Professional
 * Elegant, expensive-looking, glass morphism with sacred geometry
 */
const SpiralOrgLandingMystical = ({ onEnterProjects, onCreateManifesto, onEnterReInn }) => {
  const [spiralRotation, setSpiralRotation] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const interval = setInterval(() => {
      setSpiralRotation(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const colors = {
    violet: '#5C2D73',
    gold: '#E6C685',
    moss: '#CED3CC',
    night: '#1E2A38'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E2A38] via-[#2d1e3a] to-[#1E2A38] relative overflow-hidden">
      {/* Animated Grant Spiral Background - 50% slower */}
      <div 
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          transform: `rotate(${spiralRotation}deg) scale(1.2)`,
          transition: 'transform 0.05s linear'
        }}
      >
        <svg className="w-full h-full" viewBox="0 0 1000 1000">
          <defs>
            <linearGradient id="spiralGradientMystical" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#5C2D73', stopOpacity: 0.6 }} />
              <stop offset="50%" style={{ stopColor: '#E6C685', stopOpacity: 0.8 }} />
              <stop offset="100%" style={{ stopColor: '#5C2D73', stopOpacity: 0.6 }} />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {[...Array(12)].map((_, i) => {
            const radius = 50 + i * 40;
            const startAngle = i * 30;
            const endAngle = startAngle + 270;
            return (
              <path
                key={i}
                d={`M 500,500 m ${radius * Math.cos(startAngle * Math.PI / 180)},${radius * Math.sin(startAngle * Math.PI / 180)} 
                    a ${radius},${radius} 0 0,1 ${radius * (Math.cos(endAngle * Math.PI / 180) - Math.cos(startAngle * Math.PI / 180))},
                    ${radius * (Math.sin(endAngle * Math.PI / 180) - Math.sin(startAngle * Math.PI / 180))}`}
                fill="none"
                stroke="url(#spiralGradientMystical)"
                strokeWidth="2"
                opacity={0.8 - i * 0.06}
                filter="url(#glow)"
              />
            );
          })}
        </svg>
      </div>

      {/* Sacred Geometry Overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg className="w-full h-full">
          {[...Array(6)].map((_, i) => (
            <circle
              key={i}
              cx="50%"
              cy="50%"
              r={100 + i * 100}
              fill="none"
              stroke="#E6C685"
              strokeWidth="1"
              opacity={0.3 - i * 0.05}
            />
          ))}
        </svg>
      </div>

      {/* Parallax Particles */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`
        }}
      >
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#E6C685] rounded-full opacity-40"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 5}s infinite ease-in-out`,
              animationDelay: `${Math.random() * 5}s`
            }}
          ></div>
        ))}
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); opacity: 0.4; }
          50% { transform: translateY(-20px); opacity: 0.8; }
        }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Hero Section with Owl */}
        <div className="mb-20 relative">
          {/* Glass Morphism Container */}
          <div 
            className="rounded-3xl overflow-hidden backdrop-blur-xl bg-white/5 border border-white/10 shadow-2xl"
            style={{
              boxShadow: `0 8px 32px 0 rgba(92, 45, 115, 0.37)`
            }}
          >
            {/* Owl Image with Overlay */}
            <div className="relative h-[500px]">
              <img 
                src="/owl-hero.jpeg" 
                alt="Mystical Owl Guardian" 
                className="w-full h-full object-cover"
                style={{ 
                  filter: 'contrast(1.2) brightness(0.8)',
                  mixBlendMode: 'luminosity'
                }}
              />
              <div 
                className="absolute inset-0 bg-gradient-to-b from-[#5C2D73]/50 via-transparent to-[#1E2A38]/80"
              ></div>
              
              {/* Content Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <div className="mb-6">
                  <Sparkles className="w-16 h-16 text-[#E6C685] mx-auto animate-pulse" />
                </div>
                <h1 
                  className="text-8xl font-light tracking-[0.2em] mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#E6C685] via-[#CED3CC] to-[#E6C685]"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    textShadow: '0 0 40px rgba(230, 198, 133, 0.5)'
                  }}
                >
                  SPIRALORG
                </h1>
                <div className="w-24 h-px bg-gradient-to-r from-transparent via-[#E6C685] to-transparent mb-6"></div>
                <p 
                  className="text-2xl text-[#CED3CC] max-w-3xl leading-relaxed font-light tracking-wide"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  A guild to build belonging with logistics and song
                </p>
                <p className="text-lg text-[#E6C685]/80 mt-4 font-light">
                  Private Planetary Federation of Families
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Statement - Glass Card */}
        <div 
          className="mb-20 rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 p-10 text-center"
          style={{ boxShadow: '0 8px 32px 0 rgba(92, 45, 115, 0.37)' }}
        >
          <p 
            className="text-2xl leading-relaxed text-[#CED3CC] max-w-4xl mx-auto font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            We are about <span className="text-[#E6C685] font-semibold">agency</span>, 
            <span className="text-[#E6C685] font-semibold"> security</span>, and 
            <span className="text-[#E6C685] font-semibold"> trust</span>—
            the procreation of love and wisdom for 10,000 generations.
          </p>
          <div className="mt-6 text-[#CED3CC]/60 text-sm tracking-widest uppercase">
            By Invitation Only • Brought to you by Planetary Fed
          </div>
        </div>

        {/* Two Main Entry Paths - Premium Glass Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {/* Enter Projects */}
          <button
            onClick={onEnterProjects}
            className="group relative rounded-2xl backdrop-blur-xl bg-gradient-to-br from-[#5C2D73]/20 to-[#E6C685]/10 
              border border-[#E6C685]/30 hover:border-[#E6C685] p-12 
              transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#E6C685]/0 to-[#E6C685]/20 
              opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <Book className="w-20 h-20 mb-6 text-[#E6C685] group-hover:scale-110 transition-transform duration-300 mx-auto" />
              <h3 
                className="text-4xl mb-4 text-[#E6C685] font-light tracking-wide"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Enter Projects
              </h3>
              <div className="w-16 h-px bg-[#E6C685] mx-auto mb-4"></div>
              <p 
                className="text-lg text-[#CED3CC] leading-relaxed font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Jump straight into your work. Manage families, enterprises, and sacred collaborations 
                with our revolutionary project management system.
              </p>
            </div>
          </button>

          {/* Create Manifesto */}
          <button
            onClick={onCreateManifesto}
            className="group relative rounded-2xl backdrop-blur-xl bg-gradient-to-br from-[#5C2D73]/20 to-[#E6C685]/10 
              border border-[#5C2D73]/30 hover:border-[#5C2D73] p-12 
              transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#5C2D73]/0 to-[#5C2D73]/20 
              opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <Scroll className="w-20 h-20 mb-6 text-[#5C2D73] group-hover:scale-110 transition-transform duration-300 mx-auto" />
              <h3 
                className="text-4xl mb-4 text-[#5C2D73] font-light tracking-wide"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Create My Manifesto
              </h3>
              <div className="w-16 h-px bg-[#5C2D73] mx-auto mb-4"></div>
              <p 
                className="text-lg text-[#CED3CC] leading-relaxed font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Begin your journey. Guided by M.ai.ry to discover your purpose, 
                define your path, and connect with your people.
              </p>
            </div>
          </button>
        </div>

        {/* Rites of Presence - Premium Section */}
        <div 
          className="mb-20 rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 p-12"
          style={{ boxShadow: '0 8px 32px 0 rgba(92, 45, 115, 0.37)' }}
        >
          <div className="text-center mb-12">
            <Zap className="w-12 h-12 text-[#E6C685] mx-auto mb-4" />
            <h2 
              className="text-5xl font-light tracking-wide text-[#E6C685] mb-4"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              Rites of Presence
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-[#E6C685] to-transparent mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {/* Driftless */}
            <div className="group flex items-start gap-6 p-6 rounded-xl backdrop-blur-sm bg-white/5 
              hover:bg-white/10 transition-all duration-300 border border-white/5 hover:border-[#E6C685]/50">
              <MapPin className="w-12 h-12 text-[#E6C685] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
              <div>
                <h3 
                  className="text-2xl mb-3 text-[#E6C685] font-light"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  Driftless Region
                </h3>
                <p className="text-[#CED3CC] leading-relaxed">
                  Join Yusuf for guided explorations through the magical Driftless region 
                  of the United States. Ancient valleys, sacred springs, family homesteads.
                </p>
              </div>
            </div>

            {/* Morocco */}
            <div className="group flex items-start gap-6 p-6 rounded-xl backdrop-blur-sm bg-white/5 
              hover:bg-white/10 transition-all duration-300 border border-white/5 hover:border-[#E6C685]/50">
              <Mountain className="w-12 h-12 text-[#E6C685] flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
              <div>
                <h3 
                  className="text-2xl mb-3 text-[#E6C685] font-light"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  Heart of Morocco
                </h3>
                <p className="text-[#CED3CC] leading-relaxed">
                  Experience the depth of Moroccan culture with Yusuf. 
                  Markets, mountains, music, and mystical traditions.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <a 
              href="https://re-inn.com"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-xl bg-gradient-to-r from-[#5C2D73] to-[#E6C685] 
                text-white font-light tracking-wide text-lg hover:shadow-2xl hover:scale-105 
                transition-all duration-300 border border-[#E6C685]/50"
            >
              <Home className="w-6 h-6" />
              Explore via Re-Inn.com
            </a>
          </div>
        </div>

        {/* Quick Links Grid - Glass Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {[
            { icon: Home, title: 're-Inn Stays', desc: 'Sacred retreats & family reunions', color: '#E6C685' },
            { icon: Users, title: 'Family Federation', desc: 'AirBNB for Care', color: '#5C2D73' },
            { icon: Eye, title: 'Secret History Game', desc: 'Discover the timeline', color: '#CED3CC' }
          ].map((item, i) => (
            <div
              key={i}
              className="group rounded-xl backdrop-blur-xl bg-white/5 border border-white/10 p-8 
                hover:bg-white/10 hover:scale-105 transition-all duration-300 cursor-pointer"
            >
              <item.icon 
                className="w-14 h-14 mb-4 group-hover:scale-110 transition-transform" 
                style={{ color: item.color }}
              />
              <h4 
                className="text-xl mb-2 font-light tracking-wide"
                style={{ fontFamily: "'Cinzel', serif", color: item.color }}
              >
                {item.title}
              </h4>
              <p className="text-[#CED3CC] text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div 
          className="rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 p-10 text-center"
          style={{ boxShadow: '0 8px 32px 0 rgba(92, 45, 115, 0.37)' }}
        >
          <div className="flex justify-center gap-8 flex-wrap mb-6">
            {[
              { name: 'seedypeeps.com', url: 'https://seedypeeps.com' },
              { name: 'planetaryfed.com', url: 'https://planetaryfed.com' },
              { name: 'trustwoven.com', url: 'https://trustwoven.com' },
              { name: 're-inn.com', url: 'https://re-inn.com' }
            ].map((link, i) => (
              <a
                key={i}
                href={link.url}
                className="text-[#E6C685] hover:text-white transition-colors text-lg font-light tracking-wide"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {link.name}
              </a>
            ))}
          </div>
          <div className="text-[#CED3CC]/60 text-sm tracking-widest mb-6">
            SACRED RETREATS • FAMILY ARCHIVES • REMEDY COUNCIL
          </div>
          <button 
            className="mb-4 px-8 py-3 rounded-xl border-2 border-[#E6C685] text-[#E6C685] 
              hover:bg-[#E6C685] hover:text-[#1E2A38] transition-all duration-300 font-light tracking-wide"
          >
            Join Waitlist
          </button>
          <div className="mt-4 mb-6">
            <a href="#" className="text-[#CED3CC]/60 hover:text-[#E6C685] transition-colors text-sm">
              Click here if you have a code...
            </a>
          </div>

          {/* Version Toggle */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <button
              onClick={() => window.location.href = '?version=notebook'}
              className="px-6 py-2 bg-[#f4e8d8] text-[#5C2D73] rounded-lg 
                hover:shadow-lg hover:scale-105 transition-all text-sm font-medium"
            >
              📖 Switch to Notebook Version
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiralOrgLandingMystical;
